import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { translationService } from '../services/translationService';
import { preferenceService } from '../services/preferenceService';

// Define types directly to avoid import issues
export type Language = 'en' | 'ar';

export interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  currentLanguage: Language; // Alias for compatibility
  changeLanguage: (lang: Language) => void; // Alias for compatibility
  t: (key: string, params?: Record<string, string>) => string;
  isRTL: boolean;
  isLoading: boolean;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('ar');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize language from preferences and load translations
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        // Load saved language preference
        const preferences = preferenceService.loadPreferences();
        const savedLanguage = preferences.language || 'ar';
        
        // Load translations for the saved language
        await translationService.loadTranslations(savedLanguage);
        
        setLanguageState(savedLanguage);
        updateDocumentLanguage(savedLanguage);
        
        console.log('✅ I18n initialized with language:', savedLanguage);
      } catch (error) {
        console.warn('Failed to initialize i18n, falling back to Arabic:', error);
        // Fallback to Arabic
        try {
          await translationService.loadTranslations('ar');
          setLanguageState('ar');
          updateDocumentLanguage('ar');
        } catch (fallbackError) {
          console.error('Failed to load fallback translations:', fallbackError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, []);

  const updateDocumentLanguage = (newLanguage: Language) => {
    document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage;
  };

  const setLanguage = async (newLanguage: Language) => {
    if (newLanguage === language) return;
    
    setIsLoading(true);
    console.log('🌐 Changing language to:', newLanguage);
    
    try {
      // Load translations for the new language
      await translationService.loadTranslations(newLanguage);
      
      // Update state
      setLanguageState(newLanguage);
      updateDocumentLanguage(newLanguage);
      
      // Save preference
      preferenceService.setPreference('language', newLanguage);
      
      console.log('✅ Language changed successfully to:', newLanguage);
    } catch (error) {
      console.error('Failed to change language:', error);
      // Don't change the language if loading fails
    } finally {
      setIsLoading(false);
    }
  };

  // Translation function that uses the translation service
  const t = (key: string, params?: Record<string, string>): string => {
    return translationService.translate(key, params);
  };

  const isRTL = language === 'ar';

  // Create context value with both old and new interface for compatibility
  const contextValue: I18nContextType = {
    language,
    setLanguage,
    currentLanguage: language, // Alias for compatibility
    changeLanguage: setLanguage, // Alias for compatibility
    t,
    isRTL,
    isLoading,
  };

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};